/**
 * **手势状态的派生层 + 连线判定包装** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * `graph/canvasGesture.ts` 里是一个纯状态机，`gesture` 是它**唯一的真相**。
 * 这个文件负责两件事：
 *
 *   1. 从 `gesture` **派生**出一堆只读视图（`linkingNodeId` / `linkingType` /
 *      `linkingSide` / `linking` / `lassoMode` / `lassoPath` …）
 *   2. 把响应式状态喂给 `graph/linkRules.ts` 的纯函数，包装成
 *      `isStructurallyValid` / `alreadyConnected` / `canLink` /
 *      `isValidConnection` / `inferListItemType`
 *
 * ⚠️ 以前这里是 7 个散落的 ref（linkingNodeId / linkingHandleId / linkingType /
 * linkingSide / pendingDetach / detachedEdge / lassoMode / lassoPath），
 * 它们之间的约束只存在于作者脑子里 —— 于是「拖出去又拖回来」误删过线。
 * 现在全部**派生**，不再有「两个 ref 各说各话」的可能。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `gesture` **写多读多**（`useGestureMachine` 的 `sendGesture`、`useGrouping`
 * 的拖拽、`resetGraph`、宿主模板都在动它），所以这里把它**返回**出去，
 * 其余引用方一律从宿主拿 —— 不要在别的 ctx 里再复制一份。
 */
import { computed, ref } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowConnection as Connection } from '@/graph/flowTypes'
import {
  gestureOrigin,
  gestureWantsInputs,
  initialGestureState,
  isLinking,
} from '@/graph/canvasGesture'
import type { GestureState } from '@/graph/canvasGesture'
import {
  alreadyConnected as checkAlreadyConnected,
  canLink as checkCanLink,
  isStructurallyValid as checkStructurallyValid,
} from '@/graph/linkRules'
import { findEdgeOnInput } from '@/graph/connectionRules'
import { resolveItemType } from '@/types/socket'
import type { LinkCheckInput } from '@/graph/linkRules'
import type { SocketType } from '@/types/socket'
import type { SocketDef } from '@/types/node'

export interface GestureStateCtx {
  /** 连线的端口类型查询（useSubgraphLevels 注入） */
  socketTypeOf: (nodeId: string, handleId: string | null | undefined) => SocketType | undefined
  /** 按端口找 socket（useSubgraphLevels 注入） */
  findSocket: (nodeId: string, handleId: string | null | undefined) => SocketDef | undefined
  /** 喂给纯函数的「最小结构」输入（useSubgraphLevels 注入） */
  linkInput: (
    sourceNodeId: string,
    sourceHandleId: string | null | undefined,
    targetNodeId: string,
    targetHandleId: string | null | undefined,
  ) => LinkCheckInput
  showNotice: (message: string) => void
  /** 只为拒绝日志：查一个端口解析出来没有 */
  findSocketForLog?: (nodeId: string, handleId: string | null | undefined) => { type: string; itemType?: string } | undefined
}

export function useGestureState(ctx: GestureStateCtx) {
  const { socketTypeOf, findSocket, linkInput, showNotice, findSocketForLog } = ctx


/*
 * ── 手势状态（唯一真相在状态机里）──────────────────────────────────────────────────────────
 *
 * 以前这里是 7 个散落的 ref（linkingNodeId / linkingHandleId / linkingType /
 * linkingSide / pendingDetach / detachedEdge / lassoMode / lassoPath），
 * 它们之间的约束只存在于作者脑子里 —— 于是「拖出去又拖回来」误删过线。
 *
 * 现在全部从 graph/canvasGesture.ts 的状态**派生**：
 * 不再有「两个 ref 各说各话」的可能。
 */
const gesture = ref<GestureState>(initialGestureState())

/** 这次手势的「逻辑起点」—— 从已连线的输入拖出时，它是**原输出端口** */
const gestureOriginRef = computed(() => gestureOrigin(gesture.value))

const linkingNodeId = computed(() => gestureOriginRef.value?.nodeId ?? null)
const linkingHandleId = computed(() => gestureOriginRef.value?.handleId ?? null)
const linkingType = computed<SocketType | null>(() => {
  const origin = gestureOriginRef.value
  return origin ? socketTypeOf(origin.nodeId, origin.handleId) ?? null : null
})
/** 起点在哪一侧 → 决定高亮输入还是输出 */
const linkingSide = computed<'source' | 'target'>(() =>
  gestureWantsInputs(gesture.value) ? 'source' : 'target',
)
/** 正在连线（含「挂起待拖」与「已摘线」）—— 端口状态、变参行压紧都看它 */
const linking = computed(() => isLinking(gesture.value))

/** 划痕（模板画覆盖层用） */
const lassoMode = computed(() =>
  gesture.value.kind === 'lasso' ? gesture.value.mode : null,
)
const lassoPath = computed(() =>
  gesture.value.kind === 'lasso' ? gesture.value.path : [],
)

/*
 * 三个判定的实现在 graph/linkRules.ts（纯函数，可单测）。
 * 这里只做「把响应式状态喂进去」的包装。
 *
 * ⚠️ 关键区分（踩过一次坑）：
 *   isValidConnection 交给 Vue Flow，**只做结构校验** ——
 *   Vue Flow 会用它复检每一条已存在的边，一旦包含「是否已存在」的判断，
 *   所有边都会被判非法并整批丢弃（控制台狂刷 EDGE_INVALID）。
 *   「已经连过」只用于 connectableFor 的高亮/变暗。
 */
function isStructurallyValid(
  sourceNodeId: string,
  sourceHandleId: string | null | undefined,
  targetNodeId: string,
  targetHandleId: string | null | undefined,
): boolean {
  return checkStructurallyValid(
    linkInput(sourceNodeId, sourceHandleId, targetNodeId, targetHandleId),
  )
}

function alreadyConnected(
  sourceNodeId: string,
  sourceHandleId: string | null | undefined,
  targetNodeId: string,
  targetHandleId: string | null | undefined,
): boolean {
  return checkAlreadyConnected(
    linkInput(sourceNodeId, sourceHandleId, targetNodeId, targetHandleId),
  )
}

/** 拖拽高亮用：结构合法 且 尚未连接 */
function canLink(
  sourceNodeId: string,
  sourceHandleId: string | null | undefined,
  targetNodeId: string,
  targetHandleId: string | null | undefined,
): boolean {
  return checkCanLink(
    linkInput(sourceNodeId, sourceHandleId, targetNodeId, targetHandleId),
  )
}

/**
 * 交给 Vue Flow 的校验钩子 —— **只做结构校验**。
 * 既服务拖拽中的候选连线，也服务对既有边的复检，
 * 所以必须是「对两种情况都成立」的条件。
 */
function isValidConnection(conn: Connection): boolean {
  if (!conn.source || !conn.target) return false
  const ok = isStructurallyValid(conn.source, conn.sourceHandle, conn.target, conn.targetHandle)
  /*
   * ★ 开发期诊断：Vue Flow 会拿这个钩子**复检每一条既有边**，判非法就丢弃它，
   * 并在控制台打出误导性的 `An edge needs a source and a target`
   * （见 README「坑 7 详解」）。这里把**真正的原因**打出来 ——
   * 是两端端口没解析出来，还是类型/成环被拦。
   *
   * 只在判非法时打，正常路径零开销。
   */
  if (!ok && import.meta.env?.DEV !== false && findSocketForLog) {
    const from = findSocketForLog(conn.source, conn.sourceHandle)
    const to = findSocketForLog(conn.target, conn.targetHandle)
    console.warn(
      '[node-print] 既有边被判非法（Vue Flow 会丢弃它）:',
      `${conn.source}.${conn.sourceHandle} → ${conn.target}.${conn.targetHandle}`,
      '\n  源端口:', from ? `${from.type}${from.itemType ? `<${from.itemType}>` : ''}` : '❌ 解析不出来（端口不存在）',
      '\n  目标端口:', to ? `${to.type}${to.itemType ? `<${to.itemType}>` : ''}` : '❌ 解析不出来（端口不存在）',
    )
  }
  return ok
}

/**
 * 列表元素类型自动推导。
 * 只在目标未声明 itemType 时写入，且两侧都声明但不一致时推导失败、不做改动。
 */
function inferListItemType(
  sourceNodeId: string,
  sourceHandleId: string | null | undefined,
  targetNodeId: string,
  targetHandleId: string | null | undefined,
) {
  const source = findSocket(sourceNodeId, sourceHandleId)
  const target = findSocket(targetNodeId, targetHandleId)
  if (!source || !target) return
  if (source.type !== 'list' || target.type !== 'list') return
  if (target.itemType) return

  const resolved = resolveItemType(source.itemType, target.itemType)
  if (!resolved) return

  target.itemType = resolved
  showNotice(`已推导 ${targetHandleId} 的元素类型 → list<${resolved}>`)
}


  return {
    gesture,
    gestureOriginRef,
    linkingNodeId,
    linkingHandleId,
    linkingType,
    linkingSide,
    linking,
    lassoMode,
    lassoPath,
    isStructurallyValid,
    alreadyConnected,
    canLink,
    isValidConnection,
    inferListItemType,
  }
}
